/**
 * The person's dashboard theme.
 *
 * Remembered in two places, for two reasons. On the server (User.theme, via
 * PUT /api/auth/profile) so it follows the person to another computer — a
 * receptionist who sits at a different desk tomorrow gets their own screen.
 * And on the device (localStorage), which index.html reads before React has
 * loaded anything, so the page never flashes the default theme first.
 *
 * The server's answer arrives with the session's capabilities and wins over
 * the device's, because it is the person's choice rather than the last person
 * who used this browser.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/endpoints';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionContext';
import { applyTheme, DEFAULT_THEME, isTheme, THEME_STORAGE_KEY, themeById } from '../domain/themes';

const ThemeContext = createContext(null);

const readStored = () => {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

const store = (id) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    /* storage unavailable — the theme still applies for this visit */
  }
};

export function ThemeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { theme: serverTheme } = usePermissions();
  const [theme, setThemeState] = useState(readStored);
  const [saving, setSaving] = useState(false);

  // The account's own theme, once the server has said what it is.
  useEffect(() => {
    if (isAuthenticated && isTheme(serverTheme) && serverTheme !== theme) {
      setThemeState(serverTheme);
      store(serverTheme);
      applyTheme(serverTheme, { animate: true });
    }
    // Deliberately not depending on `theme`: this adopts the server's answer
    // when it arrives, it does not fight a choice the person just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, serverTheme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  /** Apply at once, remember on this device, then save to the account. */
  const setTheme = useCallback(
    async (id) => {
      if (!isTheme(id)) return;
      setThemeState(id);
      store(id);
      applyTheme(id, { animate: true });
      if (!isAuthenticated) return;
      setSaving(true);
      try {
        await authApi.updateProfile({ theme: id });
      } catch (err) {
        // The theme is already on screen and on this device; failing to save it
        // to the account is worth a log, not an error in the person's face.
        console.warn('[Theme] Could not save the theme to the account:', err.message);
      } finally {
        setSaving(false);
      }
    },
    [isAuthenticated],
  );

  const value = useMemo(() => ({ theme, current: themeById(theme), setTheme, saving }), [theme, setTheme, saving]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}

export default ThemeContext;
